import React, { useState, useEffect, useRef } from 'react';
import CustomerLayout from '../../components/layout/CustomerLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { MapPin, Navigation, Phone, Clock, Package, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { orderPayable } from '../../utils/orderAmounts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerTracking = () => {
  const { user } = useAuth();
  const [activeOrder, setActiveOrder] = useState(null);
  const [deliveryLocation, setDeliveryLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trackingError, setTrackingError] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    loadActiveOrder();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const loadActiveOrder = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/customer/orders`);
      const orders = res.data;
      const active = orders.find(o => o.status === 'out_for_delivery');
      if (active) {
        setActiveOrder(active);
        if (active.delivery_boy_id) {
          fetchLocation(active.delivery_boy_id);
          intervalRef.current = setInterval(() => fetchLocation(active.delivery_boy_id), 10000);
        }
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocation = async (deliveryBoyId) => {
    try {
      const res = await axios.get(`${API}/delivery/location/${deliveryBoyId}`);
      setDeliveryLocation(res.data);
      setTrackingError(null);
      updateMap(res.data.latitude, res.data.longitude);
    } catch (error) {
      if (error.response?.status === 404) {
        setTrackingError('Delivery partner location not available yet');
      }
    }
  };

  const updateMap = (lat, lng) => {
    if (!mapRef.current) return;

    if (!mapInstanceRef.current) {
      const L = window.L;
      if (!L) return;

      mapInstanceRef.current = L.map(mapRef.current).setView([lat, lng], 15);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
        maxZoom: 19,
      }).addTo(mapInstanceRef.current);

      const icon = L.divIcon({
        html: '<div style="width:32px;height:32px;background:#F97316;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-size:16px;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);">🚴</div>',
        iconSize: [32, 32],
        iconAnchor: [16, 16],
        className: '',
      });

      markerRef.current = L.marker([lat, lng], { icon }).addTo(mapInstanceRef.current);
    } else {
      markerRef.current?.setLatLng([lat, lng]);
      mapInstanceRef.current.panTo([lat, lng]);
    }
  };

  useEffect(() => {
    if (deliveryLocation && mapRef.current && !mapInstanceRef.current) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);

      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => {
        updateMap(deliveryLocation.latitude, deliveryLocation.longitude);
      };
      document.head.appendChild(script);
    }
  }, [deliveryLocation]);

  if (loading) {
    return (
      <CustomerLayout width="column" title="Track Order">
        <div className="p-4">
          <div className="animate-pulse space-y-4">
            <div className="h-64 bg-gray-200 rounded-xl" />
            <div className="h-24 bg-gray-200 rounded-xl" />
          </div>
        </div>
      </CustomerLayout>
    );
  }

  if (!activeOrder) {
    return (
      <CustomerLayout width="column" title="Track Order">
        <div className="p-4 flex flex-col items-center justify-center min-h-[60vh]">
          <Package className="w-16 h-16 text-[var(--theme-text-muted)] opacity-30 mb-4" />
          <h2 className="text-lg font-bold text-[var(--theme-text-main)] mb-2">No Active Delivery</h2>
          <p className="text-[var(--theme-text-muted)] text-center text-sm mb-6">
            You don't have any orders out for delivery right now.
            Tracking will be available once your order is dispatched.
          </p>
          <Button
            onClick={loadActiveOrder}
            variant="outline"
            className="rounded-xl"
            data-testid="refresh-tracking-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout width="column" title="Track Order">
      <div className="flex flex-col h-[calc(100vh-140px)]">
        {/* Map */}
        <div className="relative flex-1 min-h-[300px] bg-gray-100">
          {deliveryLocation ? (
            <div ref={mapRef} className="w-full h-full" data-testid="tracking-map" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center">
              <Navigation className="w-12 h-12 text-[var(--theme-text-muted)] opacity-30 mb-3" />
              <p className="text-sm text-[var(--theme-text-muted)]">
                {trackingError || 'Waiting for delivery partner location...'}
              </p>
            </div>
          )}
          {/* Refresh overlay */}
          <button
            onClick={() => activeOrder?.delivery_boy_id && fetchLocation(activeOrder.delivery_boy_id)}
            className="absolute top-3 right-3 bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg hover:bg-gray-50"
            data-testid="refresh-location-btn"
          >
            <RefreshCw className="w-4 h-4 text-[var(--theme-text-secondary)]" />
          </button>
        </div>

        {/* Order Info Card */}
        <div className="bg-[var(--theme-surface)] p-4 border-t border-black/5">
          {/* Status */}
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2.5 h-2.5 rounded-full bg-[var(--theme-primary)] animate-pulse" />
            <span className="text-sm font-semibold text-[var(--theme-primary)]">Out for Delivery</span>
            {deliveryLocation?.timestamp && (
              <span className="text-xs text-[var(--theme-text-muted)] ml-auto">
                Updated: {new Date(deliveryLocation.timestamp).toLocaleTimeString()}
              </span>
            )}
          </div>

          {/* Order details */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-bold text-[var(--theme-text-main)]">Order #{activeOrder.order_number}</p>
              <p className="text-sm text-[var(--theme-text-muted)]">
                {activeOrder.items?.length || 0} items · {activeOrder.delivery_slot_name || 'Standard'}
              </p>
            </div>
            <p className="font-bold text-lg text-[var(--theme-primary)]">
              ₹{orderPayable(activeOrder).toFixed(2)}
            </p>
          </div>

          {/* Delivery boy info */}
          {activeOrder.delivery_boy_name && (
            <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[var(--theme-primary)]/10 flex items-center justify-center">
                  <Navigation className="w-5 h-5 text-[var(--theme-primary)]" />
                </div>
                <div>
                  <p className="font-medium text-sm text-[var(--theme-text-main)]">
                    {activeOrder.delivery_boy_name}
                  </p>
                  <p className="text-xs text-[var(--theme-text-muted)]">Your delivery partner</p>
                </div>
              </div>
              {activeOrder.delivery_boy_phone && (
                <a
                  href={`tel:${activeOrder.delivery_boy_phone}`}
                  className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center"
                  data-testid="call-delivery-boy-btn"
                >
                  <Phone className="w-5 h-5 text-white" />
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </CustomerLayout>
  );
};

export default CustomerTracking;
